import React, { useState, useEffect, useCallback } from "react";
import { Typography } from "../../Components/Typography";
import { useAuth } from "../../Context/authContext";
import PersonalInfoTab from "./PersonalInfoTab";
import PasswordTab from "./PasswordTab";
import SocialsTab from "./SocialsTab";
import SkillsTab from "./SkillsTab";
import PreferencesTab from "./PreferencesTab";
import NotificationsTab from "./NotificationsTab";

const Profile = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("personal-info");
  
  const DEFAULT_AVATAR = "https://i.pravatar.cc/80?img=47";
  
  const [initialFormData, setInitialFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    role: "",
    createdAt: "",
    country: "Australia",
    state: "",
    city: "",
    timezone: "",
    bio: "",
    photo: DEFAULT_AVATAR,
    public_visibility: true,
  });

  // Get profile data from localStorage (authUser)
  const getProfileData = useCallback(() => {
    try {
      // First try to get from authUser (from login API response)
      const authUser = localStorage.getItem('authUser');
      if (authUser) {
        const userData = JSON.parse(authUser);
        return userData;
      }
      
      // Fallback to profile-specific storage
      const storedProfile = localStorage.getItem(`profile_${user?.id || user?.email}`);
      if (storedProfile) {
        const profile = JSON.parse(storedProfile);
        return profile;
      }
    } catch (error) {
      console.error("Error loading profile data:", error);
    }
    return null;
  }, [user?.id, user?.email]);

  // Save profile data to localStorage
  const saveProfileData = (data) => {
    try {
      localStorage.setItem(`profile_${user?.id || user?.email}`, JSON.stringify(data));
    } catch (error) {
      console.error("Error saving profile data:", error);
    }
  };

  // Load profile data on mount and when user changes
  useEffect(() => {
    if (user) {
      const profileDataFromStorage = getProfileData();
      
      // Get role display name based on user role
      const roleDisplayNames = {
        student: "Student",
        mentor: "Mentor",
        admin: "Administrator",
        sponsor: "Sponsor",
        investor: "Investor",
        donor: "Donor",
        ecosystem_partner: "Ecosystem Partner",
        corporate_sponsor: "Corporate Sponsor",
        program_team: "Program Team",
      };

      // Format date helper function
      const formatDate = (dateString) => {
        if (!dateString) return "";
        try {
          return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        } catch {
          return dateString;
        }
      };

      // Map mentor/user data from localStorage to form fields
      const profileData = {
        firstName: profileDataFromStorage?.first_name || user.first_name || "",
        lastName: profileDataFromStorage?.last_name || user.last_name || "",
        username: profileDataFromStorage?.username || user.username || "",
        email: profileDataFromStorage?.email || user.email || "",
        role: roleDisplayNames[profileDataFromStorage?.role || user.role] || profileDataFromStorage?.role || user.role || "",
        createdAt: formatDate(profileDataFromStorage?.created_at || user.created_at || ""),
        phone: profileDataFromStorage?.phone || user.phone || "",
        profession: profileDataFromStorage?.profession || user.profession || "",
        organization: profileDataFromStorage?.organization || user.organization || "",
        experience_years: profileDataFromStorage?.experience_years || user.experience_years || "",
        years_of_experience: profileDataFromStorage?.years_of_experience || user.years_of_experience || user.experience_years || "",
        industry_experience: profileDataFromStorage?.industry_experience || profileDataFromStorage?.industry || user.industry || "",
        functional_experience: profileDataFromStorage?.functional_experience || user.functional_experience || "",
        technology_stack: profileDataFromStorage?.technology_stack || user.technology_stack || "",
        skills: profileDataFromStorage?.skills || user.skills || [],
        expertise: profileDataFromStorage?.expertise || user.expertise || [],
        interests: profileDataFromStorage?.interests || user.interests || [],
        linkedin_url: profileDataFromStorage?.linkedin_url || user.linkedin_url || "",
        x_url: profileDataFromStorage?.x_url || user.x_url || "",
        facebook_url: profileDataFromStorage?.facebook_url || user.facebook_url || "",
        github_url: profileDataFromStorage?.github_url || user.github_url || "",
        calendly_link: profileDataFromStorage?.calendly_link || user.calendly_link || "",
        preferred_commitment: profileDataFromStorage?.preferred_commitment || user.preferred_commitment || "",
        maximum_engagements_assigned: profileDataFromStorage?.maximum_engagements_assigned || user.maximum_engagements_assigned || 3,
        mentor_status: profileDataFromStorage?.mentor_status || user.mentor_status || "",
        country: profileDataFromStorage?.country || user.country || "Australia",
        state: profileDataFromStorage?.state || user.state || "",
        city: profileDataFromStorage?.city || user.city || "",
        timezone: profileDataFromStorage?.timezone || user.timezone || "",
        bio: profileDataFromStorage?.bio || user.bio || "",
        photo: user?.profile_picture_url || profileDataFromStorage?.photo || DEFAULT_AVATAR,
        public_visibility: profileDataFromStorage?.public_visibility ?? user?.public_visibility ?? true,
      };

      setInitialFormData(profileData);
    }
  }, [user, getProfileData]);

  // If student or partner/investor and on preferences/skills tab, switch to personal-info
  useEffect(() => {
    const isStudentRole = (user?.role || "").toLowerCase() === "student";
    const isPartnerOrInvestorRole = ["ecosystem_partner", "corporate_sponsor", "investor"].includes((user?.role || "").toLowerCase());
    if ((isStudentRole && activeTab === "preferences") || (isPartnerOrInvestorRole && (activeTab === "preferences" || activeTab === "skills"))) {
      setActiveTab("personal-info");
    }
  }, [user?.role, activeTab]);

  const handleProfileUpdate = (updatedData) => {
    setInitialFormData(updatedData);
    // Save to localStorage
    saveProfileData(updatedData);
  };

  const isStudent = (user?.role || "").toLowerCase() === "student";
  const isPartnerOrInvestor = ["ecosystem_partner", "corporate_sponsor", "investor"].includes((user?.role || "").toLowerCase());
  const tabs = [
    { id: "personal-info", label: "Personal Info" },
    { id: "password", label: "Password" },
    { id: "socials", label: "Socials" },
    ...(!isPartnerOrInvestor ? [{ id: "skills", label: "Skills" }] : []),
    ...(!isStudent && !isPartnerOrInvestor ? [{ id: "preferences", label: "Preferences" }] : []),
    // { id: "notifications", label: "Notifications" },
  ];

  return (
    <div className="flex flex-col w-full h-auto gap-6 bg-primary px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between w-full">
        <Typography as="h1" className="text-2xl font-bold text-primary-900">
          Profile
        </Typography>
      </div>

      {/* Navigation Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? "text-primary-900 bg-slate-100 border-b-2 border-brand-blue"
                : "text-tertiary-600 hover:text-primary-900 hover:bg-slate-50"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === "personal-info" && (
        <PersonalInfoTab 
          initialFormData={initialFormData} 
          onUpdate={handleProfileUpdate}
        />
      )}

      {activeTab === "password" && <PasswordTab />}

      {activeTab === "skills" && !isPartnerOrInvestor && (
        <SkillsTab 
          initialFormData={initialFormData} 
          onUpdate={handleProfileUpdate}
        />
      )}

      {activeTab === "socials" && (
        <SocialsTab 
          initialFormData={initialFormData} 
          onUpdate={handleProfileUpdate}
        />
      )}

      {activeTab === "preferences" && !isStudent && !isPartnerOrInvestor && (
        <PreferencesTab 
          initialFormData={initialFormData} 
          onUpdate={handleProfileUpdate}
        />
      )}

      {activeTab === "notifications" && (
        <NotificationsTab 
          initialFormData={initialFormData} 
          onUpdate={handleProfileUpdate}
        />
      )}
    </div>
  );
};

export default Profile;

